import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ImageOff } from "lucide-react";

const STORE_DOTS = {
  Amazon: "bg-[#FF9900]",
  Flipkart: "bg-[#2874F0]",
};

export default function ProductCard({ product, index, categoryName, onBuy }) {
  const [imgError, setImgError] = useState(false);

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      whileTap={{ scale: 0.985 }}
      onClick={() => onBuy(product)}
      className="group cursor-pointer overflow-hidden rounded-3xl border border-black/5 bg-white transition-shadow duration-500 hover:shadow-[0_24px_50px_-24px_rgba(0,0,0,0.18)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
        {imgError || !product.image_url ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-zinc-300" data-testid={`product-image-fallback-${product.id}`}>
            <ImageOff className="h-8 w-8" />
            <span className="text-xs">Image unavailable</span>
          </div>
        ) : (
          <img
            src={product.image_url}
            alt={product.title}
            loading="lazy"
            onError={() => setImgError(true)}
            className="h-full w-full object-contain object-center p-4 transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            data-testid={`product-image-${product.id}`}
          />
        )}
        {categoryName && (
          <span className="absolute left-2.5 top-2.5 rounded-full border border-black/5 bg-white/90 px-2.5 py-0.5 text-[10px] font-medium tracking-wide text-zinc-600 backdrop-blur sm:left-3 sm:top-3 sm:px-3 sm:py-1 sm:text-[11px]">
            {categoryName}
          </span>
        )}
      </div>
      <div className="p-3.5 sm:p-5">
        <h3 className="font-heading text-sm font-medium leading-snug tracking-tight sm:text-base" data-testid={`product-title-${product.id}`}>
          {product.title}
        </h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 sm:mt-2">
          {product.price && (
            <span className="font-heading text-base font-semibold tabular-nums tracking-tight sm:text-lg" data-testid={`product-price-${product.id}`}>
              {product.price}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 sm:text-xs" data-testid={`product-store-${product.id}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${STORE_DOTS[product.store] || "bg-zinc-400"}`} />
            {product.store}
          </span>
        </div>
        <button
          data-testid={`buy-now-${product.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onBuy(product);
          }}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-black py-2.5 text-xs font-medium text-white transition-colors duration-200 hover:bg-zinc-800 active:scale-[0.98] sm:mt-4 sm:py-3 sm:text-sm"
        >
          Buy On
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 sm:h-4 sm:w-4" />
        </button>
      </div>
    </motion.article>
  );
}
